// ─── UNIFIED CLIENT DATA LAYER ────────────────────────────────────────────────
// Merges open jobs, history jobs, and AR aging into one record per client.
// All flags read from this unified structure.

import { normalizeName } from './clientNormalize';

export function buildUnifiedClients(openJobs, historyJobs, arData) {
  const clients = {};

  function getOrCreate(rawName) {
    const norm = normalizeName(rawName);
    if (!norm) return null;
    if (!clients[norm]) {
      clients[norm] = {
        normName: norm,
        displayName: rawName.trim().replace(/\s+/g, ' '), // clean display name
        openJobs: [],
        historyJobs: [],
        ar: null,
      };
    }
    // Keep the most common/clean display name
    if (rawName.trim().length > 0 && clients[norm].displayName.length < rawName.trim().length) {
      clients[norm].displayName = rawName.trim().replace(/\s+/g, ' ');
    }
    return clients[norm];
  }

  // Add open jobs
  (openJobs || []).forEach(j => {
    const c = getOrCreate(j.client);
    if (c) c.openJobs.push(j);
  });

  // Add history jobs
  (historyJobs || []).forEach(j => {
    const c = getOrCreate(j.client);
    if (c) c.historyJobs.push(j);
  });

  // Add AR data
  (arData || []).forEach(a => {
    const norm = normalizeName(a.client);
    if (!norm) return;
    if (!clients[norm]) {
      clients[norm] = {
        normName: norm,
        displayName: (a.client || '').trim(),
        openJobs: [],
        historyJobs: [],
        ar: null,
      };
    }
    clients[norm].ar = a;
  });

  return Object.values(clients);
}

// ─── CROSS-REFERENCED FLAG LOGIC ─────────────────────────────────────────────

export function computeUnifiedFlags(unifiedClients, today = new Date()) {
  const flags = [];

  for (const client of unifiedClients) {
    const { displayName, openJobs, historyJobs, ar } = client;
    const allJobs = [...openJobs, ...historyJobs];

    // Last activity from EITHER file
    const allDates = allJobs.filter(j => j.createdDate).map(j => j.createdDate);
    const lastJobDateMs = allDates.length > 0 ? Math.max(...allDates) : null;
    const daysSinceLastJob = lastJobDateMs
      ? Math.floor((today.getTime() - lastJobDateMs) / (1000 * 60 * 60 * 24))
      : null;

    // AR exposure
    const ar90 = ar ? ((ar.d90_180 || 0) + (ar.d180_365 || 0) + (ar.gt365 || 0)) : 0;
    const arTotal = ar ? (ar.pending || 0) : 0;

    // CROSS-REFERENCE FLAG 1: Gone quiet + has AR outstanding = escalate
    if (daysSinceLastJob !== null && daysSinceLastJob > 60 && ar90 > 0) {
      flags.push({
        type: 'quiet_with_ar',
        severity: 'red',
        client: displayName,
        message: `${displayName} — no new jobs in ${daysSinceLastJob} days AND ${ar90.toLocaleString()} in 90+ day AR`,
        action: 'Immediate escalation — client may be disengaging while debt grows',
        daysSinceLastJob,
        ar90,
      });
    }

    // CROSS-REFERENCE FLAG 2: Gone quiet (no AR issue, just quiet)
    else if (daysSinceLastJob !== null && daysSinceLastJob > 60 && allJobs.length > 0) {
      flags.push({
        type: 'gone_quiet',
        severity: daysSinceLastJob > 90 ? 'red' : 'amber',
        client: displayName,
        message: `${displayName} — no new jobs in ${daysSinceLastJob} days`,
        action: 'Schedule client meeting',
        daysSinceLastJob,
      });
    }

    // CROSS-REFERENCE FLAG 3: Has open jobs but AR aging badly = collections risk
    if (openJobs.length > 0 && ar90 > 0 && arTotal > 50000) {
      flags.push({
        type: 'active_with_overdue_ar',
        severity: 'amber',
        client: displayName,
        message: `${displayName} — active client with ${ar90.toLocaleString()} overdue 90+ days`,
        action: 'Chase AR while relationship is active',
        ar90,
        arTotal,
      });
    }

    // CROSS-REFERENCE FLAG 4: Open jobs with cost but no revenue + AR outstanding
    const costNoRevJobs = openJobs.filter(j => (j.actCost || 0) > 0 && (j.actRev || 0) === 0);
    if (costNoRevJobs.length > 0 && ar90 > 0) {
      flags.push({
        type: 'cost_no_rev_plus_ar',
        severity: 'red',
        client: displayName,
        message: `${displayName} — ${costNoRevJobs.length} jobs with cost but no revenue AND existing overdue AR`,
        action: 'Urgent: invoice immediately and chase overdue',
        jobs: costNoRevJobs,
        ar90,
      });
    }
  }

  // Deduplicate by client — keep most severe flag per client, suppress simpler ones
  const clientSeverity = {};
  for (const f of flags) {
    if (!f.client) continue;
    const key = f.client.toLowerCase().trim();
    const rank = f.severity === 'red' ? 2 : f.severity === 'amber' ? 1 : 0;
    if (!clientSeverity[key] || rank > clientSeverity[key].rank) {
      clientSeverity[key] = { rank, types: new Set([f.type]) };
    } else if (rank === clientSeverity[key].rank) {
      clientSeverity[key].types.add(f.type);
    }
  }

  // Remove gone_quiet if a more specific flag exists for same client
  const SUPERSEDED_BY = { gone_quiet: ['quiet_with_ar', 'active_with_overdue_ar', 'cost_no_rev_plus_ar'] };
  return flags.filter(f => {
    if (!f.client) return true;
    const key = f.client.toLowerCase().trim();
    const supersedes = SUPERSEDED_BY[f.type];
    if (!supersedes) return true;
    const existing = clientSeverity[key];
    if (!existing) return true;
    return !supersedes.some(t => existing.types.has(t));
  });
}

// ─── LAST ACTIVITY per client (for Client Pulse) ──────────────────────────────
export function getLastActivityMap(unifiedClients) {
  const map = {};
  for (const client of unifiedClients) {
    const allJobs = [...client.openJobs, ...client.historyJobs];
    const allDates = allJobs.filter(j => j.createdDate).map(j => j.createdDate);
    map[client.normName] = allDates.length > 0 ? Math.max(...allDates) : null;
    // Also map by display name for easy lookup
    map[client.displayName] = map[client.normName];
    map[client.displayName.trim().replace(/\s+/g, ' ')] = map[client.normName];
  }
  return map;
}
